<template>
  <form v-if="product" @submit.prevent="save">

    <modal v-if="variantsGeneratorVisible" title="Génération des déclinaisons" @close="hideVariantsGenerator">
      <variants-generator-form :value="product" @submit="saveVariants" @cancel="hideVariantsGenerator"></variants-generator-form>
    </modal>

    <modal v-if="createVariantVisible" title="Ajout d'une déclinaison" @close="hideCreateNewVariant">
      <product-variant-form :value="{published: false}" :product="product" @submit="addVariant" @cancel="hideCreateNewVariant" />
    </modal>

    <modal v-if="editVariantVisible" title="Édition d'une déclinaison" @close="hideEditVariant">
      <product-variant-form :value="editVariantVisible" :product="product" @submit="editVariant" @cancel="hideEditVariant" />
    </modal>

    <panel light>

      <h5 slot="heading" class="panel-title">{{pageTitle}}</h5>
      <div slot="heading" class="heading-elements">
        <div class="heading-buttons">
          <router-link :to="{name: 'product/edit', params: {id: product.id}}" class="btn btn-default btn-link"><i class="icon-pencil position-left"></i>Modifier le produit</router-link>
          <button class="btn btn-primary" @click="openVariantsGenerator" type="button">Générer des déclinaisons</button>
          <button class="btn btn-success" @click="openCreateNewVariant" type="button">Ajouter une déclinaison</button>
        </div>
      </div>

      <div slot="content" class="table-responsive">
        <table class="table table-striped table-lg">
          <thead>
          <tr>
            <th width="135">Image</th>
            <th>Référence / Description</th>
            <th v-for="property in productProperties" :key="property.id">
              {{property.name}}
            </th>
            <th width="100" class="text-center">Statut</th>
            <th width="80" class="text-center">Actions</th>
          </tr>
          </thead>
          <tbody v-if="product.variants.length">
          <tr v-for="(variant, index) in product.variants" :key="variant.id || index">
            <td class="centered">
              <media-image :media="variant.image" empty-label="Pas d'image" thumbnail />
            </td>
            <td>
              <h6 class="no-margin text-semibold">
                <a @click.prevent="openEditVariant(variant)">{{variant.reference || '(aucune&nbsp;référence)'}}</a>
              </h6>
              <p v-if="variant.description" class="no-margin text-muted" v-html="$options.filters.nl2br(variant.description)"></p>
            </td>
            <td v-for="property in productProperties" :key="property.id" class="text-nowrap">
              <div v-if="property.kind === 'color' && getAttributeValueForVariant(variant, property) !== '-'" class="valign-middle mr-5 img-thumbnail"><color-badge size="small" :color="getColorForVariant(variant, property)" /></div>
              <span class="valign-middle">{{getAttributeValueForVariant(variant, property)}}</span>
            </td>
            <td class="text-center">
              <publishing-status class="cursor-pointer" :value="variant.published" @hide="setVariantPublished(variant, false)" @show="setVariantPublished(variant)"></publishing-status>
            </td>
            <td class="text-center">
              <ul is="table-row-actions">
                <li><a @click="openEditVariant(variant)"><i class="icon-pencil7"></i> Modifier</a></li>
                <li class="divider"></li>
                <li><a @click="deleteVariant(variant)"><i class="icon-bin"></i> Supprimer</a></li>
              </ul>
            </td>
          </tr>
          </tbody>
          <tbody v-else>
          <tr>
            <td :colspan="4 + productProperties.length" class="text-center">
              <h4>Aucune déclinaison</h4>
              <button class="btn btn-primary" @click="openVariantsGenerator" type="button">Générer des déclinaisons</button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </panel>

  </form>
</template>

<script>
import _pick from "lodash/pick";
import _map from "lodash/map";
import _omit from "lodash/omit";
import _uniq from "lodash/uniq";

import ProductVariantsQuery from "./graphql/ProductVariantsQuery.gql";
import ProductUpdateMutation from "./graphql/ProductUpdateMutation.gql";

import {
  ProductPropertySexOptions
} from "../../common/types";

import PublishingStatus from "../../components/PublishingStatus";
import ColorBadge from "../../components/ColorBadge";
import TableRowActions from "package-common-vuejs/src/components/table/TableRowActions";

export default {
  components: {
    FormStickyHeader: () => import("../../components/FormStickyHeader.vue"),
    Modal: () => import("package-common-vuejs/src/components/Modal"),
    VariantsGeneratorForm: () => import("./VariantsGeneratorForm"),
    ProductVariantForm: () => import("./ProductVariantForm"),
    MediaImage: () => import("../../components/MediaImage"),
    ColorBadge,
    PublishingStatus,
    TableRowActions
  },
  data() {
    return {
      product: null,

      variantsGeneratorVisible: false,
      createVariantVisible: false,
      editVariantVisible: null
    };
  },
  apollo: {
    product: {
      query: ProductVariantsQuery,
      skip() {
        return !this.$route.params.id;
      },
      variables() {
        return this.$route.params;
      }
    }
  },
  computed: {
    pageTitle() {
      if (!this.product) {
        return `Déclinaisons d'un produit`;
      }
      return `Déclinaisons du produit ${this.product.name}`;
    },
    productProperties() {
      return (this.product && this.product.properties) || [];
    }
  },
  methods: {
    getAttributeValueForVariant(variant, propertyToDisplay) {
      const propertyInfos = ((variant && variant.attributes) || []).find(
        ({ property }) => property === propertyToDisplay.id
      );
      if (!propertyInfos) {
        return "-";
      }
      switch (propertyToDisplay.kind) {
        case "color":
          if (!propertyInfos.value) return '-';
          const color = (propertyToDisplay.colors || []).find(
            ({ id }) => id === propertyInfos.value
          );
          if (!color) return "(couleur non disponible)";
          return color.name || '-';
        case "sex":
          return propertyInfos.value? ProductPropertySexOptions[propertyInfos.value]: '-';
      }
      return propertyInfos.value || '-';
    },
    getColorForVariant(variant, propertyToDisplay) {
      const propertyInfos = ((variant && variant.attributes) || []).find(
        ({ property }) => property === propertyToDisplay.id
      );
      if (!propertyInfos) return null;
      if (propertyToDisplay.kind !== "color") return null;
      const color = (propertyToDisplay.colors || []).find(
        ({ id }) => id === propertyInfos.value
      );
      if (!color) return null;
      return color;
    },
    openVariantsGenerator() {
      this.variantsGeneratorVisible = true;
    },
    hideVariantsGenerator() {
      this.variantsGeneratorVisible = false;
    },
    openCreateNewVariant() {
      this.createVariantVisible = true;
    },
    hideCreateNewVariant() {
      this.createVariantVisible = false;
    },
    openEditVariant(variant) {
      this.editVariantVisible = variant;
    },
    hideEditVariant() {
      this.editVariantVisible = null;
    },
    async saveVariants(variants) {
      const data = {
        variants: _map(variants, variant => ({
          ..._omit(variant, ["__typename"]),
          image: variant.image ? variant.image.id || variant.image : null,
          attributes: _map(variant.attributes, attrs =>
            _omit(attrs, ["__typename"])
          ),
          pins: _map(variant.pins, (pin) => ({
            ..._omit(pin, ["__typename"]),
            image: typeof pin.image !== "string" ? pin.image.id : pin.image,
          })),
        })),
      };
      // Ajoute les nouvelles images à la liste
      data.images = _uniq([
        ..._map(this.product.images || [], "id"),
        ..._map(data.variants, "image")
      ]).filter(image => !!image);
      try {
        await this.$apollo.mutate({
          mutation: ProductUpdateMutation,
          variables: {
            id: this.product.id,
            data
          }
        });
        this.$store.dispatch(
          "addNotificationSuccess",
          `Les déclinaisons ont été enregistrées !`
        );
        this.hideVariantsGenerator();
        this.$apollo.queries.product.refresh();
      } catch (err) {
        this.$store.dispatch("addNotificationError", err.message);
      }
    },
    deleteVariant(variant) {
      return this.saveVariants(
        (this.product.variants || []).filter(({ id }) => variant.id !== id)
      );
    },
    addVariant(variant) {
      this.hideCreateNewVariant();
      return this.saveVariants([variant, ...(this.product.variants || [])]);
    },
    editVariant(variant) {
      this.hideEditVariant();
      const variants = this.product.variants || [];
      const variantIndex = variants.findIndex(({ id }) => id === variant.id);
      return this.saveVariants([
        ...variants.slice(0, variantIndex),
        variant,
        ...variants.slice(variantIndex + 1)
      ]);
    },
    async setVariantPublished(variant, published = true) {
      const variants = this.product.variants || [];
      const variantIndex = variants.findIndex(({ id }) => id === variant.id);
      const newVariants = [
        ...variants.slice(0, variantIndex),
        { ...variant, published },
        ...variants.slice(variantIndex + 1)
      ];
      const data = {
        variants: _map(newVariants, variant => ({
          ..._omit(variant, ["__typename"]),
          image: variant.image ? variant.image.id || variant.image : null,
          attributes: _map(variant.attributes, attrs =>
            _omit(attrs, ["__typename"])
          ),
          pins: _map(variant.pins, (pin) => ({
            ..._omit(pin, ["__typename"]),
            image: typeof pin.image !== "string" ? pin.image.id : pin.image,
          })),
        }))
      };
      try {
        await this.$apollo.mutate({
          mutation: ProductUpdateMutation,
          variables: {
            id: this.product.id,
            data
          }
        });
        this.$apollo.queries.product.refresh();
      } catch (err) {
        this.$store.dispatch("addNotificationError", err.message);
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/pages/products/ProductVariants.vue